import React, { useMemo } from 'react';
import cx from 'classnames';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { useAppSelector } from '../../../store/hooks';
import styles from './NotesForVideo.module.scss';
import { isSafari } from '../../../helpers';

type TNotesForVideo = {
  className?: string;
  switchShowNotes: () => void;
  showNotes: boolean;
};

type TRenderedNote = {
  name: string;
  message: string;
};

const NotesForVideo: React.FC<TNotesForVideo> = ({ className, switchShowNotes, showNotes }) => {
  const {
    qualifiers,
    notes,
    markerPosition: timelineMarkerPosition,
    currentPlayingTimelineClip,
  } = useAppSelector((state) => state.videoStore);
  const { composerPlayControl, composerRows } = useAppSelector((state) => state.composersStore);
  const isTimeline = window.location.pathname.includes('Timeline');
  const composerRow = composerRows[composerPlayControl.currentRowIndex];
  const composerClip = composerRow?.clips[composerPlayControl.currentClipIndex];

  const onClickHideMsg = () => {
    switchShowNotes();
  };

  const transformedNotes = useMemo(() => {
    const fallbackMessages = isTimeline
      ? (currentPlayingTimelineClip?.notes || []).map((n: any) => n?.note).filter(Boolean)
      : (composerClip?.notes || []).map((n: any) => n?.note).filter(Boolean);
    const fallbackName = isTimeline
      ? currentPlayingTimelineClip?.topicName || ''
      : composerRow?.name || '';

    const noteNames: string[] = Array.isArray(notes[0]) ? (notes[0] as string[]) : [];
    const noteMessages: string[] = (Array.isArray(notes[1]) && notes[1].length ? notes[1] : fallbackMessages) as string[];
    const headerName = noteNames[0] || fallbackName;

    if (noteMessages.length) {
      return noteMessages.map((message: string, i: number): TRenderedNote => ({
        name: i === 0 ? headerName : '',
        message: message || '',
      }));
    }

    if (headerName) {
      return [{ name: headerName, message: '' }];
    }

    return [] as TRenderedNote[];
  }, [notes, currentPlayingTimelineClip, composerClip, composerRow?.name, isTimeline]);
  const renderQualifiers = useMemo(
    () =>
      qualifiers.map((qualifier, index) => {
        const qualifierColor = qualifier.color ? `rgb(${qualifier.color.split(',').map((c) => +c * 256)})` : '';
        return (
          <span className={styles.qualifier} key={`${index}_${qualifier?.name}`} style={{ color: qualifierColor }}>
            {qualifier.name}
          </span>
        );
      }),
    [qualifiers, currentPlayingTimelineClip],
  );

  return (
    <div
      className={cx(styles.wrapper, className, {
        [styles.hide]: !showNotes || !transformedNotes?.length,
      })}
    >
      <div className={cx(styles.list)}>
        {transformedNotes?.map((note: TRenderedNote, i: number) => (
          <div key={`${note.name}_${i}_${note.message}`}>
            <span className={styles.name}>
              {note.name && `${note.name} (${isTimeline ? timelineMarkerPosition.clipIndex + 1 : composerPlayControl.currentClipIndex + 1})`}
              {note.message && (
                <>
                  {note.name && <>: <br /></>}
                  {note.message}
                </>
              )}
            </span>
          </div>
        ))}
        {renderQualifiers}
      </div>
      <div className={styles.hideMsg} onClick={onClickHideMsg}>
        <CloseOutlinedIcon />
      </div>
    </div>
  );
};

export default NotesForVideo;
